import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDevices,
  createDevice,
  updateDevice,
  deleteDevice,
} from '../redux/slices/devicesSlice';
import { devicesAPI } from '../api/api';
import { Modal, ConfirmDialog, LoadingSpinner, EmptyState } from '../components/Common';
import { useSearch } from '../context/SearchContext';
import { useEntityDetails } from '../context/EntityDetailsContext';
import { useLanguage } from '../context/LanguageContext';
import { printTable } from '../utils/printTable';

const emptyForm = { serial_number: '', brand: '', model: '' };

const Devices = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { openDevice } = useEntityDetails();
  const { t } = useLanguage();
  const { items: devices, loading, error } = useSelector((state) => state.devices);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const [serialQuery, setSerialQuery] = useState('');
  const [serialResult, setSerialResult] = useState(null);
  const [serialSearching, setSerialSearching] = useState(false);
  const [serialError, setSerialError] = useState(null);

  useEffect(() => {
    dispatch(fetchDevices());
  }, [dispatch]);

  const filtered = devices.filter((d) => {
    const term = searchTerm.toLowerCase();
    return (
      d.serial_number?.toLowerCase().includes(term) ||
      d.brand?.toLowerCase().includes(term) ||
      d.model?.toLowerCase().includes(term)
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (device) => {
    setEditingId(device.id);
    setFormData({
      serial_number: device.serial_number || '',
      brand: device.brand || '',
      model: device.model || '',
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const action = editingId
      ? updateDevice({ id: editingId, data: formData })
      : createDevice(formData);
    dispatch(action)
      .unwrap()
      .then(() => setShowModal(false))
      .catch((err) => console.error('Failed to save device:', err));
  };

  const handleDelete = () => {
    dispatch(deleteDevice(deleteTarget.id))
      .unwrap()
      .then(() => setDeleteTarget(null))
      .catch((err) => console.error('Failed to delete device:', err));
  };

  const handleSerialSearch = async (e) => {
    e.preventDefault();
    if (!serialQuery.trim()) return;
    setSerialSearching(true);
    setSerialError(null);
    setSerialResult(null);
    try {
      const { data } = await devicesAPI.getBySerial(serialQuery.trim());
      setSerialResult(data.data);
    } catch (err) {
      setSerialError(err.response?.data?.error || t('devices.noDeviceFound'));
    } finally {
      setSerialSearching(false);
    }
  };

  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('devices.errorLoading', { error })}</p>
      </div>
    );
  }

  const handlePrint = () => {
    printTable({
      title: t('devices.title'),
      columns: [
        { header: t('devices.serialNumber') },
        { header: t('devices.brand') },
        { header: t('devices.model') },
        { header: t('devices.repairs'), align: 'right' },
      ],
      rows: filtered.map((d) => [d.serial_number, d.brand, d.model, d.repair_count]),
    });
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('devices.title')}</h1>
          <p className='text-gray-600 mt-1'>
            {t('devices.subtitle')}
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={handlePrint} className='btn-secondary'>
            🖨️ {t('common.print')}
          </button>
          <button onClick={openCreate} className='btn-primary'>
            {t('devices.addDevice')}
          </button>
        </div>
      </div>

      {/* Repair-intake serial lookup */}
      <div className='card mb-6'>
        <h3 className='text-sm font-semibold text-gray-700 mb-2'>
          {t('devices.lookupTitle')}
        </h3>
        <form onSubmit={handleSerialSearch} className='flex gap-3'>
          <input
            type='text'
            value={serialQuery}
            onChange={(e) => setSerialQuery(e.target.value)}
            className='input-field flex-1'
            placeholder={t('devices.serialPlaceholder')}
          />
          <button type='submit' className='btn-secondary' disabled={serialSearching}>
            {serialSearching ? t('devices.searching') : t('common.search')}
          </button>
        </form>
        {serialError && <p className='text-sm text-red-600 mt-2'>{serialError}</p>}
        {serialResult && (
          <div
            className='mt-3 text-sm bg-green-50 border border-green-200 rounded-lg p-3 cursor-pointer hover:bg-green-100'
            onClick={() => openDevice(serialResult.id, serialResult)}
          >
            {t('devices.foundPrefix')} <span className='font-semibold'>{serialResult.brand} {serialResult.model}</span>{' '}
            {t('devices.foundSuffix', { serial: serialResult.serial_number })}
          </div>
        )}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='🔩'
          title={t('devices.noDevicesTitle')}
          message={t('devices.noDevicesMessage')}
          actionLabel={t('devices.addDevice')}
          onAction={openCreate}
          hasItems={devices.length > 0}
        />
      ) : (
        <div className='card overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('devices.serialNumber')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('devices.brand')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('devices.model')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('devices.repairs')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((d) => (
                <tr key={d.id} className='hover:bg-gray-50'>
                  <td
                    className='px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer'
                    onClick={() => openDevice(d.id, d)}
                  >
                    {d.serial_number}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.brand || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.model || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.repair_count}</td>
                  <td className='px-4 py-3 text-right space-x-3'>
                    <button onClick={() => openEdit(d)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(d)}
                      className='text-sm text-red-600 hover:text-red-800'
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? t('devices.editDevice') : t('devices.createDevice')}
        size='sm'
      >
        <form onSubmit={handleSubmit}>
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.serialNumber')} *</label>
              <input
                type='text'
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className='input-field'
                required
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.brand')}</label>
              <input
                type='text'
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className='input-field'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('devices.model')}</label>
              <input
                type='text'
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className='input-field'
              />
            </div>
            <div className='flex justify-end space-x-3 pt-4'>
              <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {editingId ? t('devices.saveChanges') : t('devices.createDevice')}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('devices.deleteDevice')}
        message={t('devices.confirmDelete', { name: deleteTarget?.serial_number })}
      />
    </div>
  );
};

export default Devices;
